import { useState, useEffect, useRef } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  PieChart, Pie, Cell, Legend,
} from "recharts";

// ── Palette ────────────────────────────────────────────────────
const P = "#6B3A8E";
const G = "#C8A951";
const PL = "#8B5AB0";
const GL = "#E5C97A";
const GRAY = "#6b7280";

// ── Shared helpers ─────────────────────────────────────────────
function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-base font-bold uppercase tracking-widest mb-6 flex items-center gap-3"
      style={{ fontFamily: "var(--font-mono)", color: G }}>
      <span className="inline-block w-6 h-px" style={{ background: G }} />
      {children}
      <span className="inline-block flex-1 h-px opacity-30" style={{ background: G }} />
    </h3>
  );
}

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, vis };
}

// Animated fill bar
function FillBar({ pct, color = P, delay = 0, label = "", value = "" }: {
  pct: number; color?: string; delay?: number; label?: string; value?: string;
}) {
  const { ref, vis } = useInView();
  return (
    <div ref={ref} className="mb-3">
      {(label || value) && (
        <div className="flex justify-between text-xs mb-1.5">
          <span className="text-gray-600 font-medium">{label}</span>
          <span className="font-mono font-bold" style={{ color }}>{value}</span>
        </div>
      )}
      <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-1000"
          style={{
            width: vis ? `${pct}%` : "0%",
            background: `linear-gradient(90deg, ${color}, ${color}cc)`,
            transitionDelay: `${delay}ms`,
          }}
        />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ART. 260 — Dixon & Hilt: Path Analysis interactivo
// ══════════════════════════════════════════════════════════════
function Art260Panel() {
  const [active, setActive] = useState<string | null>(null);
  const { ref, vis } = useInView();

  const nodes = [
    { id: "lid",  label: "Liderazgo\ndel Director", x: 8,  y: 50, color: PL },
    { id: "cli",  label: "Clima\nOrganizacional", x: 40, y: 50, color: P  },
    { id: "sat",  label: "Satisfacción\nDocente",    x: 75, y: 25, color: G  },
    { id: "des",  label: "Desempeño\nDocente",       x: 75, y: 75, color: G  },
  ];

  const paths = [
    { from: "lid", to: "cli", beta: "β = .843", sig: "p < .001", highlight: P },
    { from: "cli", to: "sat", beta: "β = .705", sig: "p < .001", highlight: G },
    { from: "cli", to: "des", beta: "β = .533", sig: "p < .001", highlight: G },
  ];

  const barData = [
    { name: "Liderazgo → Clima", beta: 0.843, fill: PL },
    { name: "Clima → Satisfacción", beta: 0.705, fill: G },
    { name: "Clima → Desempeño", beta: 0.533, fill: GL },
  ];

  const getXY = (id: string) => nodes.find(n => n.id === id)!;

  return (
    <div ref={ref} className="space-y-8">
      <SectionTitle>Modelo de Análisis de Senderos (Path Analysis)</SectionTitle>

      {/* Path diagram */}
      <div className="rounded-xl border border-[var(--border)] bg-white p-4 overflow-x-auto">
        <svg viewBox="0 0 100 100" className="w-full" style={{ minWidth: 340, height: 200 }}>
          {/* Arrows */}
          {paths.map((p) => {
            const f = getXY(p.from); const t = getXY(p.to);
            const mid = { x: (f.x + t.x) / 2, y: (f.y + t.y) / 2 };
            const isActive = active === `${p.from}-${p.to}`;
            return (
              <g key={`${p.from}-${p.to}`}
                onMouseEnter={() => setActive(`${p.from}-${p.to}`)}
                onMouseLeave={() => setActive(null)}
                style={{ cursor: "pointer" }}>
                <line x1={f.x + 8} y1={f.y} x2={t.x - 8} y2={t.y}
                  stroke={isActive ? G : "#cbd5e1"}
                  strokeWidth={isActive ? 0.8 : 0.5}
                  strokeDasharray={vis ? "none" : "100"}
                  style={{ transition: "all 0.3s" }} />
                {/* Beta label on hover */}
                {isActive && (
                  <text x={mid.x} y={mid.y - 2} textAnchor="middle"
                    fontSize="3" fontFamily="monospace" fill={G} fontWeight="bold">
                    {p.beta}
                  </text>
                )}
                <polygon
                  points={`${t.x - 9},${t.y - 1.5} ${t.x - 9},${t.y + 1.5} ${t.x - 6},${t.y}`}
                  fill={isActive ? G : "#94a3b8"} />
              </g>
            );
          })}

          {/* Nodes */}
          {nodes.map((n) => {
            const lines = n.label.split("\n");
            return (
              <g key={n.id} style={{
                opacity: vis ? 1 : 0,
                transition: `opacity 0.6s ease 0.2s`,
              }}>
                <rect x={n.x - 8} y={n.y - 7} width={16} height={14} rx={1.5}
                  fill={n.color} fillOpacity={0.15} stroke={n.color} strokeWidth={0.5} />
                {lines.map((l, i) => (
                  <text key={i} x={n.x} y={n.y - 1.5 + i * 4}
                    textAnchor="middle" fontSize="2.8"
                    fontFamily="'Inter', sans-serif" fill={n.color} fontWeight="600">
                    {l}
                  </text>
                ))}
              </g>
            );
          })}
        </svg>
        <p className="text-xs text-gray-400 text-center mt-1 font-mono">Pasa el cursor sobre las flechas para ver los coeficientes β</p>
      </div>

      {/* Beta bars */}
      <div>
        <p className="text-sm font-semibold text-gray-700 mb-4">Coeficientes de sendero estandarizados (β)</p>
        {barData.map((d, i) => (
          <FillBar key={d.name} label={d.name} value={`β = ${d.beta}`} pct={d.beta * 100} color={d.fill} delay={i * 150} />
        ))}
        <div className="mt-5 p-4 rounded-lg bg-[var(--secondary)] border border-[var(--border)]">
          <p className="text-xs font-mono text-gray-500 uppercase tracking-wide mb-1">Varianza explicada del modelo</p>
          <FillBar pct={84} color={P} label="R² total — Desempeño docente" value="84%" />
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ART. 261 — Atuesta et al.: Estilos de Liderazgo
// ══════════════════════════════════════════════════════════════
function Art261Panel() {
  const [selected, setSelected] = useState(0);
  const { ref, vis } = useInView();

  const estilos = [
    {
      nombre: "Transformacional",
      beta: 0.575, p: "p < .001", sig: true, color: P,
      desc: "Único estilo con poder predictivo significativo. Caracterizado por inspiración, motivación, estimulación intelectual y consideración individualizada.",
      subescalas: ["Influencia idealizada", "Motivación inspiracional", "Estimulación intelectual", "Consideración individual", "Atributos idealizados"],
    },
    {
      nombre: "Transaccional",
      beta: 0.089, p: "p = .348", sig: false, color: GRAY,
      desc: "No mostró poder predictivo significativo sobre el desempeño docente en este contexto.",
      subescalas: ["Recompensa contingente", "Gestión por excepción activa"],
    },
    {
      nombre: "Laissez-faire",
      beta: -0.041, p: "p = .621", sig: false, color: "#ef4444",
      desc: "Coeficiente negativo, sin significancia estadística. Ausencia de liderazgo activo.",
      subescalas: ["Evitación de decisiones", "Pasividad directiva"],
    },
  ];

  const radarData = [
    { dim: "R²", val: 33.1 },
    { dim: "β Transf.", val: 57.5 },
    { dim: "R² corr.", val: 32.4 },
    { dim: "F (15.43)", val: 77 },
    { dim: "Muestra", val: 96 },
  ];

  return (
    <div ref={ref} className="space-y-8">
      <SectionTitle>Regresión Múltiple — Estilos de Liderazgo</SectionTitle>

      {/* Selector de estilo */}
      <div className="flex gap-2 flex-wrap">
        {estilos.map((e, i) => (
          <button key={e.nombre} onClick={() => setSelected(i)}
            className="px-4 py-2 rounded-full text-sm font-semibold border-2 transition-all duration-200"
            style={{
              borderColor: selected === i ? e.color : "#e5e7eb",
              background: selected === i ? e.color : "white",
              color: selected === i ? "white" : "#374151",
            }}>
            {e.nombre}
          </button>
        ))}
      </div>

      {/* Detail card */}
      <div className="rounded-xl border-2 p-5 transition-all duration-300"
        style={{ borderColor: estilos[selected].color + "40", background: estilos[selected].color + "08" }}>
        <div className="flex items-start justify-between mb-3 flex-wrap gap-3">
          <div>
            <p className="font-bold text-lg" style={{ color: estilos[selected].color, fontFamily: "var(--font-serif)" }}>
              {estilos[selected].nombre}
            </p>
            <p className="text-xs font-mono text-gray-500 mt-0.5">{estilos[selected].p}</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold font-mono" style={{ color: estilos[selected].color }}>
              {estilos[selected].beta > 0 ? "+" : ""}{estilos[selected].beta}
            </p>
            <p className="text-xs text-gray-400">β estandarizado</p>
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-4">{estilos[selected].desc}</p>
        <FillBar
          pct={Math.abs(estilos[selected].beta) * 100}
          color={estilos[selected].color}
          label="Poder predictivo sobre desempeño docente"
          value={`|β| = ${Math.abs(estilos[selected].beta)}`}
        />
        <div className="flex flex-wrap gap-2 mt-3">
          {estilos[selected].subescalas.map((s) => (
            <span key={s} className="text-xs px-2.5 py-1 rounded-full border font-mono"
              style={{ borderColor: estilos[selected].color + "40", color: estilos[selected].color }}>
              {s}
            </span>
          ))}
        </div>
        {estilos[selected].sig && (
          <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full"
            style={{ background: P + "15", color: P }}>
            ✓ Estadísticamente significativo (p &lt; .001)
          </div>
        )}
      </div>

      {/* Comparative bar chart */}
      <div>
        <p className="text-sm font-semibold text-gray-700 mb-3">Comparación de coeficientes β</p>
        <div style={{ height: 180 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={estilos.map(e => ({ name: e.nombre, beta: Math.abs(e.beta), fill: e.color }))}
              margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#6b7280" }} />
              <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} domain={[0, 0.7]} />
              <Tooltip formatter={(v: number) => [`β = ${v}`, "Coeficiente"]} />
              <Bar dataKey="beta" radius={[4, 4, 0, 0]}>
                {estilos.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="p-4 rounded-lg bg-[var(--secondary)] border border-[var(--border)] font-mono text-xs space-y-1">
        <p className="font-bold text-gray-700">Modelo global</p>
        <p className="text-gray-500">R = .575 &nbsp;·&nbsp; R² = .331 &nbsp;·&nbsp; R²corr = .324 &nbsp;·&nbsp; F(3,92) = 15.43 &nbsp;·&nbsp; p &lt; .001</p>
        <p className="text-gray-500">n = 96 docentes · Instituciones adventistas · Santander, Colombia</p>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ART. 262 — Padilla: Inteligencia Intrapersonal & Actitud Inclusiva
// ══════════════════════════════════════════════════════════════
function Art262Panel() {
  const [hovered, setHovered] = useState<number | null>(null);
  const { ref, vis } = useInView();

  const dimensiones = [
    { nombre: "Conductual", corr: 0.68, color: P,  desc: "Mayor disposición comportamental hacia la inclusión: adaptación de estrategias, atención individualizada, colaboración con especialistas." },
    { nombre: "Afectiva",   corr: 0.55, color: PL, desc: "Sentimientos positivos hacia la diversidad y los estudiantes con NEE." },
    { nombre: "Cognitiva",  corr: 0.41, color: G,  desc: "Creencias y conocimientos sobre educación inclusiva." },
  ];

  const intrapersonalDims = [
    { nombre: "Autoconocimiento\nemocional", nivel: 82, color: P },
    { nombre: "Autorregulación", nivel: 74, color: PL },
    { nombre: "Motivación interna", nivel: 69, color: G },
  ];

  return (
    <div ref={ref} className="space-y-8">
      <SectionTitle>Correlaciones: Inteligencia Intrapersonal × Actitud Inclusiva</SectionTitle>

      {/* Correlation bars interactive */}
      <div className="space-y-3">
        <p className="text-sm font-semibold text-gray-700 mb-4">
          Fuerza de correlación por dimensión de actitud inclusiva (estimada, p &lt; .05)
        </p>
        {dimensiones.map((d, i) => (
          <div key={d.nombre}
            className="rounded-xl border-2 p-4 cursor-pointer transition-all duration-250"
            style={{
              borderColor: hovered === i ? d.color : "#e5e7eb",
              background: hovered === i ? d.color + "0a" : "white",
            }}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered(null)}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-bold text-sm" style={{ color: d.color }}>{d.nombre}</span>
              <span className="font-mono text-lg font-bold" style={{ color: d.color }}>r ≈ {d.corr}</span>
            </div>
            <div className="h-3 rounded-full bg-gray-100 overflow-hidden mb-2">
              <div className="h-full rounded-full transition-all duration-1000"
                style={{ width: vis ? `${d.corr * 100}%` : "0%", background: d.color, transitionDelay: `${i * 200}ms` }} />
            </div>
            {hovered === i && (
              <p className="text-xs text-gray-600 mt-2 animate-fade-up">{d.desc}</p>
            )}
          </div>
        ))}
      </div>

      {/* Intrapersonal dimensions */}
      <div>
        <p className="text-sm font-semibold text-gray-700 mb-4">Dimensiones de Inteligencia Intrapersonal</p>
        <div className="grid grid-cols-3 gap-3">
          {intrapersonalDims.map((d, i) => (
            <div key={d.nombre} className="rounded-xl border border-[var(--border)] p-4 text-center bg-white"
              style={{ opacity: vis ? 1 : 0, transition: `opacity 0.5s ease ${i * 150}ms` }}>
              <div className="relative mx-auto mb-3 w-16 h-16">
                <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
                  <circle cx="18" cy="18" r="15.5" fill="none" stroke="#f3f4f6" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15.5" fill="none" stroke={d.color} strokeWidth="3"
                    strokeDasharray={`${vis ? d.nivel * 0.974 : 0} 97.4`}
                    style={{ transition: `stroke-dasharray 1.2s ease ${i * 200}ms` }} />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-sm font-bold font-mono"
                  style={{ color: d.color }}>{d.nivel}%</span>
              </div>
              <p className="text-xs text-gray-600 font-medium leading-tight whitespace-pre-line">{d.nombre}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
        <strong>Hallazgo clave:</strong> La dimensión <strong>conductual</strong> mostró la correlación más fuerte con la inteligencia intrapersonal, sugiriendo que el autoconocimiento emocional se traduce directamente en prácticas inclusivas concretas.
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ART. 263 — URACCAN: Saberes e Interculturalidad
// ══════════════════════════════════════════════════════════════
function Art263Panel() {
  const [activeEje, setActiveEje] = useState(0);
  const { ref, vis } = useInView();

  const pieData = [
    { name: "Repositorio URACCAN", value: 20, color: P },
    { name: "Artículos académicos", value: 38, color: G },
  ];

  const ejes = [
    { titulo: "Gobernanza territorial", icono: "🌿", color: P,
      desc: "Vincula conocimiento académico con prácticas ancestrales de manejo de recursos naturales de pueblos indígenas y afrodescendientes." },
    { titulo: "Economía comunitaria", icono: "🤝", color: G,
      desc: "Modelos económicos basados en reciprocidad, solidaridad y bien común, en contraste con paradigmas del mercado capitalista." },
    { titulo: "Educación bilingüe", icono: "🗣️", color: PL,
      desc: "Lenguas indígenas (miskitu, mayangna, garífuna) e inglés criollo integradas como lenguas de enseñanza junto al español." },
  ];

  const timeline = [
    { año: "2001", hito: "Primeras publicaciones sistematizadas de la URACCAN" },
    { año: "2008", hito: "Consolidación del modelo de interculturalidad crítica" },
    { año: "2015", hito: "Expansión de programas de educación intercultural bilingüe" },
    { año: "2019", hito: "Plan Estratégico Institucional 2019-2023" },
    { año: "2023", hito: "Cierre del período analizado: 58 documentos revisados" },
  ];

  return (
    <div ref={ref} className="space-y-8">
      <SectionTitle>Análisis Documental URACCAN (2001–2023)</SectionTitle>

      {/* Donut + ejes */}
      <div className="grid sm:grid-cols-2 gap-6 items-center">
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-3 text-center">Fuentes documentales analizadas (N = 58)</p>
          <div style={{ height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={80}
                  dataKey="value" paddingAngle={3}
                  label={({ name, value }) => `${value}`} labelLine={false}>
                  {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Legend iconType="circle" iconSize={10} />
                <Tooltip formatter={(v) => [`${v} documentos`]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-semibold text-gray-700 mb-3">Ejes temáticos emergentes</p>
          {ejes.map((e, i) => (
            <button key={e.titulo} onClick={() => setActiveEje(i)}
              className="w-full text-left rounded-xl border-2 p-3 transition-all duration-200"
              style={{
                borderColor: activeEje === i ? e.color : "#e5e7eb",
                background: activeEje === i ? e.color + "12" : "white",
              }}>
              <span className="mr-2">{e.icono}</span>
              <span className="font-semibold text-sm" style={{ color: activeEje === i ? e.color : "#374151" }}>
                {e.titulo}
              </span>
            </button>
          ))}
          <div className="rounded-lg p-3 bg-[var(--secondary)] border border-[var(--border)] mt-2">
            <p className="text-xs text-gray-600">{ejes[activeEje].desc}</p>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div>
        <p className="text-sm font-semibold text-gray-700 mb-4">Línea de tiempo del corpus analizado</p>
        <div className="relative">
          <div className="absolute left-12 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[var(--riee-purple)] to-[var(--riee-gold)]" />
          <div className="space-y-4">
            {timeline.map((t, i) => (
              <div key={t.año} className="flex items-start gap-4 pl-2"
                style={{ opacity: vis ? 1 : 0, transform: vis ? "translateX(0)" : "translateX(-16px)", transition: `all 0.5s ease ${i * 120}ms` }}>
                <span className="w-20 text-right font-mono text-sm font-bold flex-shrink-0" style={{ color: i % 2 === 0 ? P : G }}>{t.año}</span>
                <div className="w-3 h-3 rounded-full mt-1 flex-shrink-0 z-10" style={{ background: i % 2 === 0 ? P : G }} />
                <p className="text-sm text-gray-600">{t.hito}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ART. 264 — Ruiz Sánchez: IA en Educación Primaria
// ══════════════════════════════════════════════════════════════
function Art264Panel() {
  const [tab, setTab] = useState<"riesgos" | "principios">("riesgos");
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const { ref, vis } = useInView();

  const riesgos = [
    { num: 1, titulo: "Reduccionismo pedagógico", color: "#ef4444",
      desc: "La IA cuantifica lo cuantificable, dejando fuera creatividad, empatía y pensamiento crítico." },
    { num: 2, titulo: "Brecha digital", color: "#f97316",
      desc: "La implementación requiere infraestructura no disponible equitativamente, profundizando desigualdades." },
    { num: 3, titulo: "Sesgo algorítmico", color: "#eab308",
      desc: "Algoritmos entrenados con datos históricos pueden perpetuar sesgos de género, clase, etnia o capacidad." },
    { num: 4, titulo: "Amenazas a la privacidad", color: "#8b5cf6",
      desc: "Los sistemas recopilan datos sensibles sobre menores de edad con implicaciones éticas y legales serias." },
    { num: 5, titulo: "Desplazamiento docente", color: GRAY,
      desc: "Riesgo de que la IA desplace al docente, deshumanizando la experiencia educativa." },
  ];

  const principios = [
    { num: 1, titulo: "IA como aliada, no reemplazante", color: P,
      desc: "El docente debe mantener el liderazgo pedagógico. La IA amplifica, no sustituye." },
    { num: 2, titulo: "Centralidad del vínculo humano", color: PL,
      desc: "Ninguna tecnología puede sustituir la relación docente-estudiante." },
    { num: 3, titulo: "Equidad de acceso", color: G,
      desc: "Toda implementación de IA debe ir acompañada de políticas de inclusión digital." },
    { num: 4, titulo: "Transparencia algorítmica", color: GL,
      desc: "Docentes y familias deben comprender cómo funcionan los sistemas de IA que afectan a los estudiantes." },
    { num: 5, titulo: "Formación docente continua", color: "#3b82f6",
      desc: "Los educadores deben ser preparados para usar y evaluar críticamente las herramientas de IA." },
  ];

  const items = tab === "riesgos" ? riesgos : principios;

  return (
    <div ref={ref} className="space-y-6">
      <SectionTitle>Marco Crítico-Esperanzador: IA en Educación Primaria</SectionTitle>

      {/* Tab switcher */}
      <div className="flex rounded-xl overflow-hidden border border-[var(--border)] w-fit">
        {(["riesgos", "principios"] as const).map((t) => (
          <button key={t} onClick={() => { setTab(t); setOpenIdx(null); }}
            className="px-6 py-2.5 text-sm font-semibold transition-all duration-200 capitalize"
            style={{
              background: tab === t ? (t === "riesgos" ? "#ef4444" : P) : "white",
              color: tab === t ? "white" : "#6b7280",
            }}>
            {t === "riesgos" ? "⚠ 5 Riesgos" : "✓ 5 Principios"}
          </button>
        ))}
      </div>

      {/* Accordion items */}
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={item.num}
            className="rounded-xl border-2 overflow-hidden transition-all duration-200"
            style={{
              borderColor: openIdx === i ? item.color : "#e5e7eb",
              opacity: vis ? 1 : 0,
              transform: vis ? "translateX(0)" : "translateX(20px)",
              transition: `border-color 0.2s, opacity 0.5s ease ${i * 80}ms, transform 0.5s ease ${i * 80}ms`,
            }}>
            <button
              className="w-full flex items-center gap-4 p-4 text-left"
              onClick={() => setOpenIdx(openIdx === i ? null : i)}>
              <span className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                style={{ background: item.color }}>
                {item.num}
              </span>
              <span className="font-semibold text-sm text-gray-800 flex-1">{item.titulo}</span>
              <span className="text-gray-400 text-lg">{openIdx === i ? "−" : "+"}</span>
            </button>
            {openIdx === i && (
              <div className="px-16 pb-4 animate-fade-up">
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Summary visual */}
      <div className="rounded-xl p-4 border border-[var(--border)] bg-[var(--secondary)]">
        <p className="text-xs font-mono uppercase tracking-wide text-gray-500 mb-3">Postura del artículo</p>
        <div className="flex items-center gap-3">
          <div className="flex-1 h-3 rounded-full overflow-hidden bg-gray-200">
            <div className="h-full rounded-full" style={{ width: "60%", background: `linear-gradient(90deg, #ef4444, ${G})` }} />
          </div>
          <span className="text-xs font-bold font-mono" style={{ color: G }}>Crítico-Esperanzadora</span>
        </div>
        <div className="flex justify-between text-xs text-gray-400 mt-1 font-mono">
          <span>Alerta crítica</span>
          <span>Esperanza pedagógica</span>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ART. 265 — Girarte Guillén: Modelo Ético-Pedagógico
// ══════════════════════════════════════════════════════════════
function Art265Panel() {
  const [activePilar, setActivePilar] = useState<number | null>(null);
  const { ref, vis } = useInView();

  const pilares = [
    { id: 0, titulo: "Prudencia Tecnológica", icono: "🔍", color: P,
      sub: "Uso discernido y crítico de las herramientas de IA",
      detalle: "Implica evaluar las herramientas de IA con criterios éticos y pedagógicos antes de adoptarlas, evitando la adopción acrítica por presión tecnológica." },
    { id: 1, titulo: "Formación del Carácter", icono: "⭐", color: G,
      sub: "Desarrollo de virtudes éticas en el estudiante",
      detalle: "Centra el proceso educativo en el desarrollo de virtudes como la honestidad intelectual, la responsabilidad digital y el discernimiento moral ante los desafíos de la IA." },
    { id: 2, titulo: "Justicia Digital", icono: "⚖️", color: PL,
      sub: "Acceso equitativo y uso responsable de la IA",
      detalle: "Garantiza que los beneficios de la IA en educación superior lleguen equitativamente a todos los estudiantes, sin reproducir desigualdades existentes." },
  ];

  const fases = [
    { fase: "Fase 1", accion: "Diagnóstico institucional", color: P },
    { fase: "Fase 2", accion: "Formación docente en ética de IA", color: PL },
    { fase: "Fase 3", accion: "Integración curricular guiada por el modelo", color: G },
  ];

  return (
    <div ref={ref} className="space-y-8">
      <SectionTitle>Modelo Ético-Pedagógico Trifásico</SectionTitle>

      {/* Triangle / three pillars */}
      <div>
        <p className="text-sm font-semibold text-gray-700 mb-4 text-center">Los tres pilares del modelo — haz clic para explorar</p>
        <div className="grid grid-cols-3 gap-3">
          {pilares.map((p, i) => (
            <button key={p.id} onClick={() => setActivePilar(activePilar === i ? null : i)}
              className="rounded-2xl border-2 p-4 text-center transition-all duration-300 flex flex-col items-center"
              style={{
                borderColor: activePilar === i ? p.color : "#e5e7eb",
                background: activePilar === i ? p.color + "12" : "white",
                opacity: vis ? 1 : 0,
                transform: vis ? "translateY(0) scale(1)" : "translateY(20px) scale(0.95)",
                transition: `all 0.5s ease ${i * 150}ms`,
              }}>
              <span className="text-3xl mb-2">{p.icono}</span>
              <p className="font-bold text-xs leading-tight" style={{ color: activePilar === i ? p.color : "#374151" }}>
                {p.titulo}
              </p>
            </button>
          ))}
        </div>

        {activePilar !== null && (
          <div className="mt-4 rounded-xl border-2 p-5 animate-fade-up"
            style={{ borderColor: pilares[activePilar].color + "50", background: pilares[activePilar].color + "08" }}>
            <p className="font-semibold text-sm mb-1" style={{ color: pilares[activePilar].color }}>
              {pilares[activePilar].titulo}
            </p>
            <p className="text-xs text-gray-500 italic mb-2">{pilares[activePilar].sub}</p>
            <p className="text-sm text-gray-700">{pilares[activePilar].detalle}</p>
          </div>
        )}
      </div>

      {/* Implementation phases */}
      <div>
        <p className="text-sm font-semibold text-gray-700 mb-4">Fases de implementación propuestas</p>
        <div className="space-y-3">
          {fases.map((f, i) => (
            <div key={f.fase} className="flex items-center gap-4"
              style={{ opacity: vis ? 1 : 0, transform: vis ? "translateX(0)" : "translateX(-20px)", transition: `all 0.5s ease ${i * 150}ms` }}>
              <div className="w-20 flex-shrink-0 text-center py-2 rounded-lg text-xs font-bold text-white"
                style={{ background: f.color }}>{f.fase}</div>
              <div className="h-0.5 w-6 flex-shrink-0" style={{ background: f.color }} />
              <div className="flex-1 rounded-lg border border-[var(--border)] bg-white px-4 py-2 text-sm text-gray-700">
                {f.accion}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Radar */}
      <div>
        <p className="text-sm font-semibold text-gray-700 mb-3 text-center">Dimensiones del marco teórico</p>
        <div style={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={[
              { dim: "Ética", val: 90 },
              { dim: "Pedagogía", val: 85 },
              { dim: "Cosmovisión cristiana", val: 95 },
              { dim: "Justicia digital", val: 75 },
              { dim: "Alfabetización digital", val: 80 },
              { dim: "Discernimiento moral", val: 88 },
            ]}>
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis dataKey="dim" tick={{ fontSize: 10, fill: "#6b7280" }} />
              <PolarRadiusAxis tick={false} domain={[0, 100]} />
              <Radar dataKey="val" stroke={P} fill={P} fillOpacity={0.2} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ART. 266 — Beskow: Integración de Fe y Aprendizaje
// ══════════════════════════════════════════════════════════════
function Art266Panel() {
  const [selectedDis, setSelectedDis] = useState<number | null>(null);
  const [activeVia, setActiveVia] = useState(0);
  const { ref, vis } = useInView();

  const disciplinas = [
    { nombre: "Ciencias naturales", icono: "🔬", color: P },
    { nombre: "Matemáticas", icono: "📐", color: PL },
    { nombre: "Ciencias sociales", icono: "🌍", color: G },
    { nombre: "Educación", icono: "🎓", color: GL },
    { nombre: "Humanidades", icono: "📖", color: "#3b82f6" },
    { nombre: "Artes", icono: "🎨", color: "#ec4899" },
    { nombre: "Tecnología", icono: "💻", color: "#06b6d4" },
    { nombre: "Ciencias de la salud", icono: "🏥", color: "#10b981" },
    { nombre: "Administración", icono: "📊", color: "#f59e0b" },
    { nombre: "Teología", icono: "✝️", color: "#8b5cf6" },
    { nombre: "Derecho", icono: "⚖️", color: "#64748b" },
    { nombre: "Comunicación", icono: "📡", color: "#f43f5e" },
  ];

  const vias = [
    { num: 1, titulo: "Formación docente inicial y continua", color: P,
      desc: "Incorporar la IFA en los programas de formación de educadores adventistas para que integren su fe en la práctica académica desde el inicio de su carrera." },
    { num: 2, titulo: "Diseño curricular", color: G,
      desc: "Adoptar los principios de IFA en el diseño de planes de estudio, desde la definición de competencias hasta la selección de metodologías y materiales." },
    { num: 3, titulo: "Cultura institucional", color: PL,
      desc: "Fomentar una cultura organizacional donde la integración de fe y aprendizaje trascienda el aula y permeé la vida comunitaria de toda la institución." },
  ];

  return (
    <div ref={ref} className="space-y-8">
      <SectionTitle>Integración de Fe y Aprendizaje — 12 Disciplinas</SectionTitle>

      {/* 12 disciplines grid */}
      <div>
        <p className="text-sm font-semibold text-gray-700 mb-3">Vol. 2 — Aplicaciones por área disciplinar (haz clic)</p>
        <div className="grid grid-cols-4 gap-2">
          {disciplinas.map((d, i) => (
            <button key={d.nombre} onClick={() => setSelectedDis(selectedDis === i ? null : i)}
              className="rounded-xl border-2 p-2.5 text-center transition-all duration-200 flex flex-col items-center"
              style={{
                borderColor: selectedDis === i ? d.color : "#e5e7eb",
                background: selectedDis === i ? d.color + "15" : "white",
                opacity: vis ? 1 : 0,
                transform: vis ? "scale(1)" : "scale(0.8)",
                transition: `all 0.4s ease ${i * 40}ms`,
              }}>
              <span className="text-xl">{d.icono}</span>
              <span className="text-[10px] font-medium leading-tight mt-1" style={{ color: d.color }}>{d.nombre}</span>
            </button>
          ))}
        </div>
        {selectedDis !== null && (
          <div className="mt-3 rounded-xl border-2 p-4 animate-fade-up"
            style={{ borderColor: disciplinas[selectedDis].color + "40" }}>
            <p className="text-sm font-bold" style={{ color: disciplinas[selectedDis].color }}>
              {disciplinas[selectedDis].icono} IFA en {disciplinas[selectedDis].nombre}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              El Vol. 2 presenta marcos y estrategias específicas para integrar fe y aprendizaje en {disciplinas[selectedDis].nombre.toLowerCase()}, reconociendo que la IFA adopta formas y énfasis distintos según el campo de conocimiento.
            </p>
          </div>
        )}
      </div>

      {/* 3 implementation pathways */}
      <div>
        <p className="text-sm font-semibold text-gray-700 mb-4">3 Vías de implementación propuestas</p>
        <div className="flex gap-2 mb-4 flex-wrap">
          {vias.map((v, i) => (
            <button key={v.num} onClick={() => setActiveVia(i)}
              className="flex-1 min-w-24 py-2 px-3 rounded-lg border-2 text-xs font-bold transition-all"
              style={{ borderColor: activeVia === i ? v.color : "#e5e7eb", background: activeVia === i ? v.color : "white", color: activeVia === i ? "white" : "#6b7280" }}>
              Vía {v.num}
            </button>
          ))}
        </div>
        <div className="rounded-xl border-2 p-5 transition-all duration-300"
          style={{ borderColor: vias[activeVia].color + "40", background: vias[activeVia].color + "08" }}>
          <p className="font-bold text-sm mb-2" style={{ color: vias[activeVia].color }}>
            Vía {vias[activeVia].num}: {vias[activeVia].titulo}
          </p>
          <p className="text-sm text-gray-600">{vias[activeVia].desc}</p>
        </div>
      </div>

      {/* Volumes summary */}
      <div className="grid sm:grid-cols-2 gap-4">
        {[
          { vol: "Vol. 1", titulo: "Fundamentos filosóficos y teológicos", color: P, items: ["Presupuestos epistemológicos", "Estrategias pedagógicas", "Desafíos futuros"] },
          { vol: "Vol. 2", titulo: "Aplicaciones disciplinares", color: G, items: ["12 grupos disciplinares", "Marcos específicos por área", "Recursos prácticos docentes"] },
        ].map((v) => (
          <div key={v.vol} className="rounded-xl border-2 p-4" style={{ borderColor: v.color + "30", background: v.color + "06" }}>
            <p className="font-bold text-sm mb-2" style={{ color: v.color }}>{v.vol} — {v.titulo}</p>
            <ul className="space-y-1">
              {v.items.map((item) => (
                <li key={item} className="flex items-center gap-2 text-xs text-gray-600">
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: v.color }} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ROUTER — Selecciona el panel correcto por ID de artículo
// ══════════════════════════════════════════════════════════════
export default function InteractivePanel({ articleId }: { articleId: number }) {
  const panels: Record<number, React.ReactNode> = {
    260: <Art260Panel />,
    261: <Art261Panel />,
    262: <Art262Panel />,
    263: <Art263Panel />,
    264: <Art264Panel />,
    265: <Art265Panel />,
    266: <Art266Panel />,
  };

  const panel = panels[articleId];
  if (!panel) return null;

  return (
    <div className="rounded-2xl border border-[var(--border)] bg-white p-6 shadow-sm">
      {panel}
    </div>
  );
}
